import React, { useState, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import { useNavigate, useLocation, Link } from "react-router-dom";
import "../styles/Header.scss";
import "../styles/Notifications.scss";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import { useDispatch, useSelector } from "react-redux";
import { fetchBreadcrumbs } from "../redux/breadcrumbsSlice";
import Breadcrumbs from "./Breadcrumbs";
import { searchContent } from "../helper/apicalls/search";
import LaunchIcon from "@mui/icons-material/Launch";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SchoolIcon from "@mui/icons-material/School";
import QuizIcon from "@mui/icons-material/Quiz";
import FolderIcon from "@mui/icons-material/Folder";
import "../styles/Navbar.scss";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { checkIsAdmin } from "../helper/authUtils";
import { clearPageIdCookies } from "../helper/cookieUtils";
import AssignmentIcon from "@mui/icons-material/Assignment";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DoneIcon from "../assets/png/mark_as_read.png";
import {
  getNotifications,
  markNotificationAsRead,
  createNotificationsSocket,
} from "../helper/apicalls/notifications";
import { formatUtcToIstNotificationTime } from "../helper/dateUtils";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import BoltIcon from "@mui/icons-material/Bolt";
import ArticleIcon from "@mui/icons-material/Article";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import BookIcon from "@mui/icons-material/Book";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ConfirmationPopup from "./confirmationPopup";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import edluminaHorizontalLogo from "../assets/png/edluminaHorizontalLogo.png";
import Tooltip from "@mui/material/Tooltip";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [userCoins, setUserCoins] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [readNotifications, setReadNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [activeNotificationTab, setActiveNotificationTab] = useState("unread");
  const [isLearningZoneExpanded, setIsLearningZoneExpanded] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const notificationsSocketRef = useRef(null);
  const { data: breadcrumbData, currentCourseKey } = useSelector(
    (state) => state.breadcrumbs,
  );

  const pathnames = location.pathname.split("/").filter((x) => x);
  let coursekey = Cookies.get("courseKey");

  const isDashboardRoute = location.pathname === "/dashboard";
  const isProfileRoute = location.pathname === "/profile";
  if (!coursekey) {
    if (pathnames.length > 0) {
      if (
        (pathnames[0] === "courses" ||
          pathnames[0] === "materials" ||
          pathnames[0] === "question-bank") &&
        pathnames.length > 1
      ) {
        coursekey = pathnames[1];
      } else if (
        pathnames[0] !== "courses" ||
        pathnames[0] !== "materials" ||
        pathnames[0] !== "question-bank"
      ) {
        coursekey = pathnames[0];
      }
    }
  }

  useEffect(() => {
    if (
      (coursekey &&
        coursekey !== currentCourseKey &&
        location.pathname.includes("course")) ||
      (location.pathname.includes("materials") &&
        !location.pathname.includes("overall-materials")) ||
      location.pathname.includes("question-bank")
    ) {
      dispatch(fetchBreadcrumbs(coursekey));
    }
  }, [coursekey, currentCourseKey, dispatch, location.pathname]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSearch = (e) => {
    e.preventDefault();
  };

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.trim().length > 0) {
        setIsSearching(true);
        setShowResults(true);
        try {
          const key = coursekey;
          const data = await searchContent(key, searchQuery);

          const results = Array.isArray(data)
            ? data
            : data && Array.isArray(data.data)
              ? data.data
              : [];

          setSearchResults(results);
        } catch (error) {
          console.error("Search error:", error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, coursekey]);

  const handleResultClick = (result) => {
    if (result.path) {
      const cleanPath = result.path.replace(/\/+$/, "");
      const navigatePath = `/courses${cleanPath}`;

      navigate(navigatePath);
      setShowResults(false);
      setSearchQuery("");
      setIsSearchExpanded(false);
    }
  };

  useEffect(() => {
    const handleClickOutsideSearch = (event) => {
      if (
        !event.target.closest(".header__search-container") &&
        !event.target.closest(".header__search-icon-only")
      ) {
        setIsSearchExpanded(false);
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideSearch);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideSearch);
    };
  }, []);

  useEffect(() => {
    const handleClickOutsideUser = (event) => {
      if (!event.target.closest(".header__user-wrapper")) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideUser);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideUser);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMenuOpen &&
        !event.target.closest(".header__mobile-menu") &&
        !event.target.closest(".header__mobile-toggle")
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleClickOutsideNotifications = (event) => {
      if (!event.target.closest(".header__notifications-wrapper")) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideNotifications);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutsideNotifications,
      );
    };
  }, []);

  useEffect(() => {
    const token = Cookies.get("accessToken");
    if (!token) {
      setNotificationsLoading(false);
      return;
    }
    setNotificationsLoading(true);

    const socket = createNotificationsSocket(
      (payload) => {
        const source =
          payload && payload.data && !payload.unread_notifications
            ? payload.data
            : payload;

        const incomingUnread = Array.isArray(source?.unread_notifications)
          ? source.unread_notifications
          : [];
        const incomingRead = Array.isArray(source?.read_notifications)
          ? source.read_notifications
          : [];

        setUnreadNotifications((prev) => {
          if (!prev || prev.length === 0) {
            return incomingUnread;
          }

          const existingIds = new Set(
            prev.map((item) => item.notification_id || item.id || item._id),
          );

          const fresh = incomingUnread.filter((item) => {
            const itemId = item.notification_id || item.id || item._id;
            return itemId && !existingIds.has(itemId);
          });

          return [...fresh, ...prev];
        });

        setReadNotifications((prev) => {
          if (!prev || prev.length === 0) {
            return incomingRead;
          }

          const existingIds = new Set(
            prev.map((item) => item.notification_id || item.id || item._id),
          );

          const fresh = incomingRead.filter((item) => {
            const itemId = item.notification_id || item.id || item._id;
            return itemId && !existingIds.has(itemId);
          });

          return [...fresh, ...prev];
        });
        setActiveNotificationTab("unread");
        setNotificationsLoading(false);
      },
      () => {
        setNotificationsLoading(false);
      },
    );

    notificationsSocketRef.current = socket;

    return () => {
      if (notificationsSocketRef.current) {
        if (
          notificationsSocketRef.current.readyState === WebSocket.OPEN ||
          notificationsSocketRef.current.readyState === WebSocket.CONNECTING
        ) {
          notificationsSocketRef.current.close();
        }
        notificationsSocketRef.current = null;
      }
    };
  }, []);

  const isFolderRoute =
    location.pathname.startsWith("/materials-folder/") ||
    location.pathname.startsWith("/question-bank-folder/");

  const syncUserCoinsFromStorage = () => {
    try {
      if (checkIsAdmin()) {
        setUserCoins(null);
        return;
      }
      const storedUser = Cookies.get("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        const coins =
          parsedUser && typeof parsedUser.total_coins === "number"
            ? parsedUser.total_coins
            : 0;
        setUserCoins((prev) => (prev !== coins ? coins : prev));
      } else {
        setUserCoins((prev) => (prev !== null ? null : prev));
      }
    } catch (error) {
      console.error("Failed to parse user from cookies:", error);
      setUserCoins(null);
    }
  };

  useEffect(() => {
    syncUserCoinsFromStorage();

    const handleStorage = (event) => {
      if (event.key === "user") {
        syncUserCoinsFromStorage();
      }
    };

    window.addEventListener("storage", handleStorage);

    const intervalId = setInterval(syncUserCoinsFromStorage, 2000);

    return () => {
      window.removeEventListener("storage", handleStorage);
      clearInterval(intervalId);
    };
  }, []);

  const getFolderName = () => {
    const folderName = Cookies.get("currentFolderName") || "Folder";
    return folderName;
  };
  const navItems = [
    {
      id: 1,
      text: "Dashboard",
      path: "/dashboard",
      icon: <DashboardIcon />,
    },
    {
      id: 2,
      text: "Course",
      path: `/courses/${coursekey}`,
      icon: <SchoolIcon />,
    },
    {
      id: 3,
      text: "Homeworks",
      path: "/homeworks",
      icon: <AssignmentIcon />,
    },
    {
      id: 4,
      text: "Guides and Materials",
      path: coursekey
        ? `/overall-materials/${coursekey}`
        : "/overall-materials",
      icon: <BookIcon />,
    },
    {
      id: 5,
      text: "Current Affairs",
      path: coursekey ? `/current-affairs/${coursekey}` : "/current-affairs",
      icon: <ArticleIcon />,
    },
    {
      id: 6,
      text: "Videos",
      path: coursekey ? `/videos/${coursekey}` : "/videos",
      icon: <VideoFileIcon />,
    },
    {
      id: 7,
      text: "Learning Zone",
      path: "/active-recall",
      icon: <LocalLibraryIcon />,
    },
    {
      id: 8,
      text: "Materials",
      path: coursekey ? `/materials/${coursekey}` : "/materials",
      icon: <FolderIcon />,
    },
    {
      id: 9,
      text: "Question Bank",
      path: coursekey ? `/question-bank/${coursekey}` : "/question-bank",
      icon: <QuizIcon />,
    },
  ].filter((item) => {
    if (checkIsAdmin()) {
      return item.text === "Course";
    }
    return true;
  });

  return (
    <header className="header">
      <div className="header__container">
        <div className="header__content">
          <div
            className={`header__left ${isSearchExpanded ? "header__left--search-expanded" : ""}`}
          >
            <button
              className="header__mobile-toggle"
              onClick={toggleMenu}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              <MenuIcon />
            </button>
            {!isSearchExpanded && (
              <>
                {isFolderRoute ? (
                  <div className="header__folder-nav">
                    <IconButton
                      aria-label="Back"
                      onClick={handleBackClick}
                      className="header__back"
                      size="small"
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <h1
                      className="header__folder-title"
                      title={getFolderName()}
                    >
                      {getFolderName()}
                    </h1>
                  </div>
                ) : isDashboardRoute ? (
                  <div className="header__title-group">
                    <Tooltip title="Dashboard" arrow>
                      <h1 className="header__page-title">Dashboard</h1>
                    </Tooltip>
                  </div>
                ) : isProfileRoute ? (
                  <div className="header__title-group">
                    <Tooltip title="Profile" arrow>
                      <h1 className="header__page-title">Profile</h1>
                    </Tooltip>
                  </div>
                ) : location.pathname.startsWith("/gamification") ? (
                  <div className="header__title-group">
                    <Tooltip title="Skill Arena" arrow>
                      <h1 className="header__page-title">Skill Arena</h1>
                    </Tooltip>
                  </div>
                ) : location.pathname.startsWith("/overall-test") ? (
                  <div className="header__title-group">
                    <Tooltip title="Test" arrow>
                      <h1 className="header__page-title">Test</h1>
                    </Tooltip>
                  </div>
                ) : location.pathname.startsWith("/homeworks") ? (
                  <div className="header__title-group">
                    <Tooltip title="Homeworks" arrow>
                      <h1 className="header__page-title">Homeworks</h1>
                    </Tooltip>
                  </div>
                ) : location.pathname.startsWith("/overall-materials") ? (
                  <div className="header__title-group">
                    <Tooltip title="Guides and Materials" arrow>
                      <h1 className="header__page-title">
                        Guides and Materials
                      </h1>
                    </Tooltip>
                  </div>
                ) : location.pathname.startsWith("/active-recall") ? (
                  <div className="header__title-group">
                    <Tooltip title="Active Recall" arrow>
                      <h1 className="header__page-title">Active Recall</h1>
                    </Tooltip>
                  </div>
                ) : location.pathname.startsWith("/adaptive-learning") ? (
                  <div className="header__title-group">
                    <Tooltip title="Adaptive Learning" arrow>
                      <h1 className="header__page-title">Adaptive Learning</h1>
                    </Tooltip>
                  </div>
                ) : location.pathname.startsWith("/current-affairs") ? (
                  <div className="header__title-group">
                    <Tooltip title="Current Affairs" arrow>
                      <h1 className="header__page-title">Current Affairs</h1>
                    </Tooltip>
                  </div>
                ) : location.pathname.startsWith("/videos") ? (
                  <div className="header__title-group">
                    <Tooltip title="Videos" arrow>
                      <h1 className="header__page-title">Videos</h1>
                    </Tooltip>
                  </div>
                ) : (
                  <div className="header__title-group">
                    <Breadcrumbs breadcrumbData={breadcrumbData} />
                  </div>
                )}
              </>
            )}
          </div>

          <div className="header__right">
            {!isSearchExpanded && userCoins !== null && (
              <div className="header__coins-badge">
                <div className="header__coins-icon">
                  <MonetizationOnIcon fontSize="small" />
                </div>
                <div className="header__coins-info">
                  <span className="header__coins-value">
                    {userCoins}
                    <span className="header__credits-text"> Credits</span>
                  </span>
                </div>
              </div>
            )}
            {!isSearchExpanded ? (
              <IconButton
                size="small"
                className="header__search-icon-only"
                onClick={() => setIsSearchExpanded(true)}
                aria-label="Search"
              >
                <SearchIcon />
              </IconButton>
            ) : (
              <div className="header__search-container header__search-expanded">
                <form className="header__search-form" onSubmit={handleSearch}>
                  <input
                    type="text"
                    className="header__search-input"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => {
                      if (searchQuery.trim().length > 0) setShowResults(true);
                    }}
                    autoFocus
                  />
                  <IconButton
                    type="submit"
                    className="header__search-button"
                    aria-label="Search"
                    size="small"
                  >
                    <SearchIcon />
                  </IconButton>
                </form>

                {showResults && searchQuery.trim().length > 0 && (
                  <div className="header__search-results custom-scrollbar">
                    {isSearching ? (
                      <div className="header__search-item">
                        <div className="header__search-item-content">
                          <div className="header__search-item-title">
                            Searching...
                          </div>
                        </div>
                      </div>
                    ) : searchResults.length > 0 ? (
                      searchResults.map((result, index) => (
                        <div
                          key={index}
                          className="header__search-item"
                          onClick={() => handleResultClick(result)}
                        >
                          <div className="header__search-item-content">
                            <div className="header__search-item-title">
                              {result.name}
                            </div>
                            <div className="header__search-item-type">
                              {result.current_layer
                                ? result.current_layer.charAt(0).toUpperCase() +
                                  result.current_layer.slice(1)
                                : "Item"}
                            </div>
                          </div>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResultClick(result);
                            }}
                            className="header__search-item-action"
                          >
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                        </div>
                      ))
                    ) : (
                      <div className="header__search-item">
                        <div className="header__search-item-content">
                          <div className="header__search-item-title">
                            Search not found
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="header__notifications-wrapper">
              <IconButton
                size="small"
                className="header__notifications-button"
                onClick={() => {
                  setShowNotifications((v) => !v);
                }}
              >
                <NotificationsIcon />
              </IconButton>
              {unreadNotifications.length > 0 && (
                <span className="header__notifications-count">
                  {unreadNotifications.length}
                </span>
              )}
              {showNotifications && (
                <div className="header__notifications-dropdown">
                  <div className="notifications-tabs">
                    <button
                      type="button"
                      className={`notifications-tab ${
                        activeNotificationTab === "unread" ? "active" : ""
                      }`}
                      onClick={() => setActiveNotificationTab("unread")}
                    >
                      Unread ({unreadNotifications.length})
                    </button>
                    <button
                      type="button"
                      className={`notifications-tab ${
                        activeNotificationTab === "read" ? "active" : ""
                      }`}
                      onClick={() => setActiveNotificationTab("read")}
                    >
                      Read ({readNotifications.length})
                    </button>
                  </div>

                  {notificationsLoading ? (
                    <div className="notifications-empty">Loading...</div>
                  ) : activeNotificationTab === "unread" ? (
                    unreadNotifications.length === 0 ? (
                      <div className="notifications-empty">
                        No unread notifications
                      </div>
                    ) : (
                      <ul className="notifications-list custom-scrollbar">
                        {unreadNotifications.map((n) => {
                          const id = n.notification_id || n.id || n._id;
                          const title = n.message || n.title || "Notification";
                          const contextName = n.name || "";
                          const nType = n.notification_type || n.type || "";
                          const createdAt =
                            n.created_at ||
                            n.createdAt ||
                            n.timestamp ||
                            n.time ||
                            n.date;

                          const formattedTime =
                            formatUtcToIstNotificationTime(createdAt);

                          return (
                            <li
                              key={id || title}
                              className="notifications-item unread"
                            >
                              <div className="notifications-main-wrapper">
                                <div
                                  className="notifications-main"
                                  onClick={() => {
                                    if (nType === "test") {
                                      navigate(
                                        coursekey
                                          ? `/overall-test/${coursekey}`
                                          : "/overall-test",
                                      );
                                    } else if (nType === "blog") {
                                      navigate(
                                        coursekey
                                          ? `/current-affairs/${coursekey}`
                                          : "/current-affairs",
                                      );
                                    }
                                    setShowNotifications(false);
                                  }}
                                  style={{ cursor: "pointer" }}
                                >
                                  {contextName && (
                                    <span className="notifications-context">
                                      {contextName}
                                    </span>
                                  )}
                                  <span
                                    className="notifications-title"
                                    title={title}
                                  >
                                    {title}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  className="notifications-mark-read-btn"
                                  onClick={async () => {
                                    if (!id) return;
                                    try {
                                      await markNotificationAsRead(id);
                                      setNotificationsLoading(true);
                                      const data = await getNotifications();
                                      const source =
                                        data &&
                                        data.data &&
                                        !data.unread_notifications
                                          ? data.data
                                          : data;

                                      const refreshedUnread = Array.isArray(
                                        source?.unread_notifications,
                                      )
                                        ? source.unread_notifications
                                        : [];
                                      const refreshedRead = Array.isArray(
                                        source?.read_notifications,
                                      )
                                        ? source.read_notifications
                                        : [];

                                      setUnreadNotifications(refreshedUnread);
                                      setReadNotifications(refreshedRead);
                                    } catch (e) {
                                      console.error(
                                        "Failed to mark notification as read",
                                        e,
                                      );
                                    } finally {
                                      setNotificationsLoading(false);
                                    }
                                  }}
                                  aria-label="Mark as read"
                                >
                                  <img src={DoneIcon} alt="Done" />
                                </button>
                              </div>
                              {(formattedTime || nType) && (
                                <div className="notifications-meta">
                                  <span className="notifications-time">
                                    {formattedTime}
                                  </span>{" "}
                                  <span
                                    className={`notifications-type-chip ${nType.toLowerCase()}`}
                                  >
                                    {nType.toLowerCase() === "blog"
                                      ? "Current Affairs"
                                      : nType}
                                  </span>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )
                  ) : readNotifications.length === 0 ? (
                    <div className="notifications-empty">
                      No read notifications
                    </div>
                  ) : (
                    <ul className="notifications-list custom-scrollbar">
                      {readNotifications.map((n) => {
                        const id = n.notification_id || n.id || n._id;
                        const title = n.message || n.title || "Notification";
                        const contextName = n.name || "";
                        const nType = n.notification_type || n.type || "";
                        const createdAt =
                          n.created_at ||
                          n.createdAt ||
                          n.timestamp ||
                          n.time ||
                          n.date;

                        const formattedTime =
                          formatUtcToIstNotificationTime(createdAt);

                        return (
                          <li
                            key={id || title}
                            className="notifications-item read"
                          >
                            <div className="notifications-main-wrapper">
                              <div
                                className="notifications-main"
                                onClick={() => {
                                  if (nType === "test") {
                                    navigate(
                                      coursekey
                                        ? `/overall-test/${coursekey}`
                                        : "/overall-test",
                                    );
                                  } else if (nType === "blog") {
                                    navigate(
                                      coursekey
                                        ? `/current-affairs/${coursekey}`
                                        : "/current-affairs",
                                    );
                                  }
                                  setShowNotifications(false);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {contextName && (
                                  <span className="notifications-context">
                                    {contextName}
                                  </span>
                                )}
                                <span
                                  className="notifications-title"
                                  title={title}
                                >
                                  {title}
                                </span>
                                {(formattedTime || nType) && (
                                  <div className="notifications-meta">
                                    <span className="notifications-time">
                                      {formattedTime}
                                    </span>{" "}
                                    <span
                                      className={`notifications-type-chip ${nType.toLowerCase()}`}
                                    >
                                      {nType.toLowerCase() === "blog"
                                        ? "Current Affairs"
                                        : nType}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div className="header__user-wrapper">
              <button
                type="button"
                className="header__avatar-btn"
                onClick={() => setShowUserMenu((v) => !v)}
                aria-label="User menu"
              >
                {(() => {
                  try {
                    const user = Cookies.get("user") || {};
                    const parsed = JSON.parse(user);
                    const name =
                      parsed.name ||
                      parsed.full_name ||
                      parsed.username ||
                      "User";
                    const initials = String(name)
                      .split(" ")
                      .map((n) => n && n[0])
                      .filter(Boolean)
                      .slice(0, 2)
                      .join("")
                      .toUpperCase();
                    return (
                      <span className="header__avatar-circle">{initials}</span>
                    );
                  } catch (e) {
                    return <span className="header__avatar-circle">U</span>;
                  }
                })()}
              </button>
              {showUserMenu && (
                <div className="header__user-dropdown">
                  {(() => {
                    const user = Cookies.get("user") || {};
                    const parsed = JSON.parse(user);
                    const name =
                      parsed.name ||
                      parsed.full_name ||
                      parsed.username ||
                      "User";
                    const email = parsed.email || parsed.mail || "";
                    return (
                      <>
                        <div className="header__user-meta">
                          <div className="header__user-name">{name}</div>
                          {email && (
                            <div className="header__user-email">{email}</div>
                          )}
                        </div>
                        <Link
                          to="/profile"
                          className="header__user-item"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <span className="header__user-item-icon">
                            <PersonOutlineIcon fontSize="small" />
                          </span>
                          <span className="header__user-item-text">
                            Profile
                          </span>
                        </Link>
                        <button
                          type="button"
                          className="header__user-item"
                          onClick={() => {
                            setOpenLogoutDialog(true);
                          }}
                        >
                          <span className="header__user-item-icon">
                            <LogoutIcon fontSize="small" />
                          </span>
                          <span className="header__user-item-text">Logout</span>
                        </button>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <>
          <div className="header__mobile-overlay" onClick={toggleMenu}>
            <nav
              className="header__mobile-menu"
              onClick={(e) => e.stopPropagation()}
            >
              <Link to="/dashboard" className="navbar-logo" onClick={() => {
                clearPageIdCookies();
                toggleMenu();
              }}>
                <img
                  src={edluminaHorizontalLogo}
                  className="navbar-logo-img"
                  alt="Edlumina logo"
                />
              </Link>

              <div className="navbar-menu custom-scrollbar">
                {navItems.map((item) => {
                  const isActive =
                    (item.text === "Materials" &&
                      location.pathname.startsWith("/materials")) ||
                    (item.text === "Materials" &&
                      location.pathname.startsWith("/materials-folder")) ||
                    (item.text === "Question Bank" &&
                      location.pathname.startsWith("/question-bank")) ||
                    (item.text === "Question Bank" &&
                      location.pathname.startsWith("/question-bank-folder")) ||
                    (item.text === "Learning Zone" &&
                      (location.pathname.startsWith("/gamification") ||
                        location.pathname.startsWith("/active-recall") ||
                        location.pathname.startsWith("/adaptive-learning"))) ||
                    (item.text !== "Materials" &&
                      item.text !== "Question Bank" &&
                      item.text !== "Learning Zone" &&
                      location.pathname.startsWith(item.path));

                  const isActiveRecallActive =
                    location.pathname.startsWith("/active-recall");
                  const isAdaptiveLearningActive =
                    location.pathname.startsWith("/adaptive-learning");

                  if (item.text === "Learning Zone") {
                    return (
                      <React.Fragment key={item.id}>
                        <div className="navbar-learning-zone-header">
                          <Link
                            to={item.path}
                            className={`navbar-link ${
                              isActive ? "active" : ""
                            }`}
                            onClick={() => {
                              setIsLearningZoneExpanded((prev) => !prev);
                              clearPageIdCookies();
                              toggleMenu();
                            }}
                          >
                            <span className="nav-icon">{item.icon}</span>
                            <div className="expanded-wrapper">
                              <span className="nav-text">{item.text}</span>
                              <button
                                type="button"
                                className="navbar-learning-zone-toggle"
                              >
                                {isLearningZoneExpanded ? (
                                  <ExpandLessIcon fontSize="small" />
                                ) : (
                                  <ExpandMoreIcon fontSize="small" />
                                )}
                              </button>
                            </div>
                          </Link>
                        </div>
                        {isLearningZoneExpanded && (
                          <>
                            {/* <Link
                              key="learning-zone-skill-arena-mobile"
                              to="/gamification"
                              className={`navbar-link navbar-link-child ${
                                isSkillArenaActive ? "active" : ""
                              }`}
                            >
                              <span className="nav-icon">
                                <SportsEsportsIcon fontSize="small" />
                              </span>
                              <span className="nav-text">Skill arena</span>
                            </Link> */}
                            <Link
                              key="learning-zone-active-recall-mobile"
                              to="/active-recall"
                              className={`navbar-link navbar-link-child ${
                                isActiveRecallActive ? "active" : ""
                              }`}
                              onClick={() => {
                                clearPageIdCookies();
                                toggleMenu();
                              }}
                            >
                              <span className="nav-icon">
                                <BoltIcon fontSize="small" />
                              </span>
                              <span className="nav-text">Active recall</span>
                            </Link>
                            <Link
                              key="learning-zone-adaptive-learning-mobile"
                              to="/adaptive-learning"
                              className={`navbar-link navbar-link-child ${
                                isAdaptiveLearningActive ? "active" : ""
                              }`}
                              onClick={() => {
                                clearPageIdCookies();
                                toggleMenu();
                              }}
                            >
                              <span className="nav-icon">
                                <AutoGraphIcon fontSize="small" />
                              </span>
                              <span className="nav-text">
                                Adaptive learning
                              </span>
                            </Link>
                          </>
                        )}
                      </React.Fragment>
                    );
                  }

                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={`navbar-link ${isActive ? "active" : ""}`}
                      onClick={() => {
                        if (item.text !== "Course") {
                          clearPageIdCookies();
                        }
                        toggleMenu();
                      }}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      <span className="nav-text">{item.text}</span>
                    </Link>
                  );
                })}
              </div>
            </nav>
          </div>
        </>
      )}

      <ConfirmationPopup
        open={openLogoutDialog}
        onClose={() => {
          if (logoutLoading) return;
          setOpenLogoutDialog(false);
        }}
        onConfirm={async () => {
          if (logoutLoading) return;
          try {
            setLogoutLoading(true);
            await import("../helper/apicalls/auth").then((m) => m.logout());
            window.location.href = "/login";
          } catch (e) {
            setLogoutLoading(false);
          }
        }}
        title="Confirm Logout"
        message="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
        variant="warning"
        loading={logoutLoading}
      />
    </header>
  );
};

export default Header;
